//
// # AgentHierarchyPanel ("Agents" tab)
//
// Live view of the running-agent hierarchy for this conversation. The root is
// the ide-user agent; each node can be expanded to reveal the agents it
// spawned. Each row has TWO independent disclosures:
//
//   - the left chevron expands/collapses this node's CHILDREN (tree structure);
//   - clicking the node header toggles this node's INFO (description, scope,
//     turn count, and actions). Rows are compact by default so the tree stays
//     scannable; expand a node only when you want its detail or to act on it.
//
// Per-node actions:
//   - Stop / Retire: one state-driven button. An active agent (running/idle/
//     sleeping) can be Stopped (halts it and everything beneath it); an inactive
//     agent (done/stopped) can be Retired (evicts its whole subtree). The root
//     can't be retired.
//   - Message: talk to one agent directly. A warning notes the agent's parents
//     won't see the message in their context, which may disrupt them.
//
// Stopping is cooperative and takes effect only at the agent's next
// checkpoint, so there's a lag between "stop requested" and the agent actually
// halting. We bridge that gap with a client-side "Stopping…" state: the moment
// Stop is clicked the subtree is marked stopping, and each id is cleared once
// the pushed tree shows it inactive.
//
// Data flows two ways (see Workspace): a pull on open/tab-focus/reconnect
// (fetchAgentTree) and live pushes from the server (setAgentTree). Each node's
// "last tool" reflects the most recent tool the agent invoked — a lightweight
// activity signal that doesn't expose the agent's reasoning.
//

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronRight, ChevronDown } from "lucide-react";
import type { AgentStatus, AgentTreeNode } from "AgentRunTreeSchema";

import { useWorkspace } from "../../context";
import "./agentHierarchyPanel.css";

// An agent is "active" (alive) while running, idle, or sleeping (waiting out a
// deferred `delay` timer); once done or stopped it is inactive and can be retired.
function isActive(status: AgentStatus): boolean {
  return status === "running" || status === "idle" || status === "sleeping";
}

// Human label for the run state.
function statusLabel(status: AgentStatus): string {
  switch (status) {
    case "running":
      return "Working";
    case "idle":
      return "Idle";
    case "sleeping":
      return "Sleeping";
    case "done":
      return "Done";
    case "stopped":
      return "Stopped";
  }
}

// Every agentId in a subtree, including the root of that subtree.
function subtreeIds(node: AgentTreeNode): string[] {
  const out: string[] = [node.agentId];
  for (const child of node.children) out.push(...subtreeIds(child));
  return out;
}

// Ids of every node in a subtree that HAS children — i.e. the nodes whose
// children-chevron can be expanded. Used by "Expand all".
function parentNodeIds(node: AgentTreeNode): string[] {
  const out: string[] = [];
  if (node.children.length > 0) out.push(node.agentId);
  for (const child of node.children) out.push(...parentNodeIds(child));
  return out;
}

function StatusBadge({
  status,
  stopping,
}: {
  readonly status: AgentStatus;
  readonly stopping: boolean;
}) {
  // A stop was requested but the agent hasn't checkpointed yet.
  if (stopping && isActive(status)) {
    return (
      <span className="agent-status agent-status-stopping">Stopping…</span>
    );
  }
  return (
    <span className={`agent-status agent-status-${status}`}>
      {statusLabel(status)}
    </span>
  );
}

interface AgentRowProps {
  readonly node: AgentTreeNode;
  readonly depth: number;
  readonly isRoot: boolean;
  readonly expandedChildren: ReadonlySet<string>;
  readonly expandedInfo: ReadonlySet<string>;
  readonly stopping: ReadonlySet<string>;
  readonly onToggleChildren: (agentId: string) => void;
  readonly onToggleInfo: (agentId: string) => void;
  readonly onStop: (node: AgentTreeNode) => void;
  readonly onRetire: (agentId: string) => void;
  readonly onMessage: (node: AgentTreeNode) => void;
}

function AgentRow({
  node,
  depth,
  isRoot,
  expandedChildren,
  expandedInfo,
  stopping,
  onToggleChildren,
  onToggleInfo,
  onStop,
  onRetire,
  onMessage,
}: AgentRowProps) {
  const hasChildren = node.children.length > 0;
  const childrenOpen = expandedChildren.has(node.agentId);
  const infoOpen = expandedInfo.has(node.agentId);
  const isStopping = stopping.has(node.agentId) && isActive(node.status);
  const active = isActive(node.status);
  const indent = depth * 16 + 8;

  return (
    <>
      <div className="agent-row" style={{ paddingLeft: indent }}>
        {hasChildren ? (
          <button
            className="agent-row-toggle"
            title={childrenOpen ? "Collapse children" : "Expand children"}
            onClick={() => {
              onToggleChildren(node.agentId);
            }}
          >
            {childrenOpen ? (
              <ChevronDown size={13} />
            ) : (
              <ChevronRight size={13} />
            )}
          </button>
        ) : (
          <span className="agent-row-toggle-spacer" />
        )}

        <button
          className="agent-row-header"
          title={infoOpen ? "Hide details" : "Show details"}
          onClick={() => {
            onToggleInfo(node.agentId);
          }}
        >
          <span className="agent-row-name">{node.name}</span>
          <StatusBadge status={node.status} stopping={isStopping} />
          <span className="agent-info-caret">
            {infoOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </span>
        </button>
      </div>

      {infoOpen && (
        <div className="agent-row-info" style={{ paddingLeft: indent + 20 }}>
          {node.description && (
            <div className="agent-row-desc">{node.description}</div>
          )}
          <div className="agent-row-meta">
            {node.scopeLabel && (
              <span className="agent-row-scope">scope: {node.scopeLabel}</span>
            )}
            <span className="agent-row-turns">turn {node.turnCount}</span>
          </div>
          <div className="agent-row-tool">
            last tool:{" "}
            {node.lastTool ? (
              <code className="agent-row-tool-name">{node.lastTool}</code>
            ) : (
              <span className="agent-row-tool-none">none yet</span>
            )}
          </div>
          <div className="agent-row-actions">
            {active ? (
              <button
                className="agent-action-btn"
                disabled={isStopping}
                title="Stop this agent and everything beneath it"
                onClick={() => {
                  onStop(node);
                }}
              >
                {isStopping ? "Stopping…" : "Stop"}
              </button>
            ) : (
              <button
                className="agent-action-btn"
                disabled={isRoot}
                title={
                  isRoot
                    ? "The root agent can't be retired"
                    : "Retire this agent and its whole subtree"
                }
                onClick={() => {
                  onRetire(node.agentId);
                }}
              >
                Retire
              </button>
            )}
            <button
              className="agent-action-btn"
              disabled={isRoot}
              title={
                isRoot
                  ? "Talk to the root agent from the main chat"
                  : "Message this agent directly"
              }
              onClick={() => {
                onMessage(node);
              }}
            >
              Message
            </button>
          </div>
        </div>
      )}

      {hasChildren &&
        childrenOpen &&
        node.children.map((child) => (
          <AgentRow
            key={child.agentId}
            node={child}
            depth={depth + 1}
            isRoot={false}
            expandedChildren={expandedChildren}
            expandedInfo={expandedInfo}
            stopping={stopping}
            onToggleChildren={onToggleChildren}
            onToggleInfo={onToggleInfo}
            onStop={onStop}
            onRetire={onRetire}
            onMessage={onMessage}
          />
        ))}
    </>
  );
}

// Command-palette-style box for messaging one agent directly. Multi-line, so
// Enter inserts a newline and Cmd/Ctrl+Enter sends; Escape cancels.
function MessageAgentDialog({
  agentName,
  onSend,
  onClose,
}: {
  readonly agentName: string;
  readonly onSend: (prompt: string) => void;
  readonly onClose: () => void;
}) {
  const [text, setText] = useState("");

  const submit = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed) return;
    onSend(trimmed);
    onClose();
  }, [text, onSend, onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        submit();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    },
    [submit, onClose]
  );

  return createPortal(
    <div
      className="command-palette-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={handleKeyDown}
    >
      <div className="command-palette add-instance-dialog" role="dialog">
        <div className="command-palette-prompt-label">Message {agentName}</div>
        <div className="agent-message-warning">
          ⚠ This goes straight to this agent. Its parent agents won&apos;t have
          this message in their context, which may disrupt their workflow.
        </div>
        <textarea
          className="command-palette-input agent-message-input"
          placeholder="Message this agent…"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
          }}
          rows={4}
          autoFocus
        />
        <div className="add-instance-footer">
          <span className="add-instance-hint">
            Cmd/Ctrl+Enter to send · Escape to cancel
          </span>
          <button
            className="add-instance-btn"
            onClick={submit}
            disabled={!text.trim()}
          >
            Send
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function AgentHierarchyPanel({
  visible,
}: {
  readonly visible: boolean;
}) {
  const workspace = useWorkspace();

  // The tree rides its own subscription (not WorkspaceSnapshot) so its ~10Hz
  // churn during a run re-renders only this panel. Fires immediately with the
  // current tree, then on every push/pull.
  const [agentTree, setAgentTreeState] = useState<AgentTreeNode | null>(null);
  useEffect(() => workspace.subscribeAgentTree(setAgentTreeState), [workspace]);

  // Pull a fresh tree when the tab becomes visible (open / tab-focus /
  // reconnect). Live updates otherwise arrive via push.
  useEffect(() => {
    if (visible) void workspace.fetchAgentTree();
  }, [visible, workspace]);

  // Children expansion — the root is expanded by default once it appears.
  const [expandedChildren, setExpandedChildren] = useState<ReadonlySet<string>>(
    new Set()
  );
  // Per-node info disclosure — collapsed by default to keep rows compact.
  const [expandedInfo, setExpandedInfo] = useState<ReadonlySet<string>>(
    new Set()
  );
  // Agents with a stop requested but not yet honored (client-side "Stopping…").
  const [stopping, setStopping] = useState<ReadonlySet<string>>(new Set());

  const rootId = agentTree?.agentId;
  useEffect(() => {
    if (rootId === undefined) return;
    setExpandedChildren((prev) => {
      if (prev.has(rootId)) return prev;
      const next = new Set(prev);
      next.add(rootId);
      return next;
    });
  }, [rootId]);

  // Reconcile the per-node UI sets against the live tree: prune expansion ids
  // for agents that have dropped out (retired/gone) so they don't accumulate,
  // and clear "Stopping…" for any agent that's now inactive (actually stopped)
  // or gone.
  useEffect(() => {
    const present = new Set<string>();
    const active = new Set<string>();
    const walk = (n: AgentTreeNode) => {
      present.add(n.agentId);
      if (isActive(n.status)) active.add(n.agentId);
      n.children.forEach(walk);
    };
    if (agentTree) walk(agentTree);

    const pruneTo = (
      setter: React.Dispatch<React.SetStateAction<ReadonlySet<string>>>,
      allowed: ReadonlySet<string>
    ) => {
      setter((prev) => {
        if (prev.size === 0) return prev;
        let changed = false;
        const next = new Set<string>();
        for (const id of prev) {
          if (allowed.has(id)) {
            next.add(id);
          } else {
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    };

    pruneTo(setExpandedChildren, present);
    pruneTo(setExpandedInfo, present);
    pruneTo(setStopping, active);
  }, [agentTree]);

  const [messageTarget, setMessageTarget] = useState<{
    agentId: string;
    name: string;
  } | null>(null);

  const makeToggle = (
    setter: React.Dispatch<React.SetStateAction<ReadonlySet<string>>>
  ) => {
    return (agentId: string) => {
      setter((prev) => {
        const next = new Set(prev);
        if (next.has(agentId)) {
          next.delete(agentId);
        } else {
          next.add(agentId);
        }
        return next;
      });
    };
  };
  const onToggleChildren = useMemo(() => makeToggle(setExpandedChildren), []);
  const onToggleInfo = useMemo(() => makeToggle(setExpandedInfo), []);

  // "Expand all" targets: every node's info, and every node that has children.
  const allIds = useMemo(
    () => (agentTree ? subtreeIds(agentTree) : []),
    [agentTree]
  );
  const parentIds = useMemo(
    () => (agentTree ? parentNodeIds(agentTree) : []),
    [agentTree]
  );
  const allExpanded =
    agentTree !== null &&
    allIds.every((id) => expandedInfo.has(id)) &&
    parentIds.every((id) => expandedChildren.has(id));

  const onToggleExpandAll = useCallback(() => {
    if (allExpanded) {
      setExpandedChildren(new Set());
      setExpandedInfo(new Set());
    } else {
      setExpandedChildren(new Set(parentIds));
      setExpandedInfo(new Set(allIds));
    }
  }, [allExpanded, parentIds, allIds]);

  const onStop = useCallback(
    (node: AgentTreeNode) => {
      // Mark the whole subtree stopping — Stop halts the node and everything
      // beneath it. Each id clears when the pushed tree shows it inactive.
      const ids = subtreeIds(node);
      setStopping((prev) => {
        const next = new Set(prev);
        for (const id of ids) next.add(id);
        return next;
      });
      void workspace.stopAgent(node.agentId);
    },
    [workspace]
  );
  const onRetire = useCallback(
    (agentId: string) => {
      void workspace.retireAgent(agentId);
    },
    [workspace]
  );
  const onMessage = useCallback((node: AgentTreeNode) => {
    setMessageTarget({ agentId: node.agentId, name: node.name });
  }, []);

  return (
    <div className="agent-hierarchy-panel">
      <div className="agent-hierarchy-header">
        <span className="agent-hierarchy-title">Agents</span>
        {agentTree && (
          <button
            className="agent-hierarchy-expand-all"
            onClick={onToggleExpandAll}
          >
            {allExpanded ? "Collapse all" : "Expand all"}
          </button>
        )}
      </div>
      <div className="agent-hierarchy-body">
        {agentTree ? (
          <AgentRow
            node={agentTree}
            depth={0}
            isRoot={true}
            expandedChildren={expandedChildren}
            expandedInfo={expandedInfo}
            stopping={stopping}
            onToggleChildren={onToggleChildren}
            onToggleInfo={onToggleInfo}
            onStop={onStop}
            onRetire={onRetire}
            onMessage={onMessage}
          />
        ) : (
          <div className="agent-hierarchy-empty">No active agents.</div>
        )}
      </div>

      {messageTarget && (
        <MessageAgentDialog
          agentName={messageTarget.name}
          onSend={(prompt) => {
            void workspace.messageAgent(messageTarget.agentId, prompt);
          }}
          onClose={() => {
            setMessageTarget(null);
          }}
        />
      )}
    </div>
  );
}
